import type { CSSProperties, ReactNode } from "react";
import { variableDisplayLabel, variableKeyFromDisplay } from "%/contractTemplate";

export const CONTRACT_A4_WIDTH = 794;
export const CONTRACT_A4_HEIGHT = 1123;
export const CONTRACT_PAGE_MARGIN = 75;
export const CONTRACT_BODY_WIDTH = CONTRACT_A4_WIDTH - CONTRACT_PAGE_MARGIN * 2;
export const CONTRACT_EDITOR_FONT =
  "Malgun Gothic, '맑은 고딕', 'Apple SD Gothic Neo', sans-serif";

const TEXT_MARKER_RE = /\{\{\s*([^}]+?)\s*\}\}|\[([a-zA-Z][a-zA-Z0-9_]*)\]/g;

export type ContractFieldRenderer = (
  node: any,
  key: string,
) => ReactNode;

export type ContractVariableRenderer = (
  varKey: string,
  label: string | undefined,
  key: string,
) => ReactNode;

interface Props {
  doc: any;
  scale?: number;
  variables?: Record<string, string | null | undefined> | null;
  renderField?: ContractFieldRenderer;
  renderVariable?: ContractVariableRenderer;
  variableMode?: "label" | "value";
  pageShadow?: boolean;
  /**
   * true(기본): 페이지를 A4 고정 크기로 고정하고 넘치는 내용은 잘라낸다(편집기 배경용).
   * false: 변수 값이 채워져 줄이 늘어도 잘리지 않게 페이지가 세로로 자란다(서명/열람용).
   */
  clipPages?: boolean;
}

const textValue = (v: string | null | undefined): string =>
  v != null && v !== "" ? String(v) : "";

const markStyle = (marks?: any[]): CSSProperties => {
  const style: CSSProperties = {};
  if (!Array.isArray(marks)) return style;

  const decorations: string[] = [];
  for (const mark of marks) {
    if (mark?.type === "bold") style.fontWeight = 700;
    if (mark?.type === "italic") style.fontStyle = "italic";
    if (mark?.type === "underline") decorations.push("underline");
    if (mark?.type === "strike") decorations.push("line-through");
    if (mark?.type === "textStyle") {
      const attrs = mark.attrs || {};
      if (attrs.color) style.color = attrs.color;
      if (attrs.fontFamily) style.fontFamily = attrs.fontFamily;
      if (attrs.fontSize) style.fontSize = attrs.fontSize;
    }
  }
  if (decorations.length) style.textDecoration = decorations.join(" ");
  return style;
};

const defaultVariableRenderer = (
  variables: Props["variables"],
  mode: Props["variableMode"],
): ContractVariableRenderer =>
  (varKey, label, key) => {
    const value = textValue(variables?.[varKey]);
    if (mode === "value" && value) {
      return <span key={key}>{value}</span>;
    }
    return (
      <span
        key={key}
        className="bg-[#FFF7E6] font-semibold text-[#FF9934]"
        style={{ padding: "0 2px", borderRadius: 2, lineHeight: "inherit" }}
      >
        {label || variableDisplayLabel(varKey)}
      </span>
    );
  };

const defaultFieldRenderer: ContractFieldRenderer = (node, key) => {
  const a = node.attrs || {};
  const type = a.fieldType || "text";
  const color = fieldColor(type);
  const label = a.label || a.fieldKey || "필드";
  // 단일레이어(inline) 필드는 x/y 가 없으므로 본문 흐름 안 inline 정적 박스로 렌더.
  if (a.x == null && a.y == null) {
    return (
      <span
        key={key}
        data-contract-field="true"
        data-field-type={type}
        className="inline-block rounded border-2 border-dashed bg-white px-1.5 py-0.5 align-middle font-semibold"
        style={{ borderColor: color, color, fontSize: 10, lineHeight: 1.2, verticalAlign: "middle" }}
      >
        {fieldIcon(type)} {label}
      </span>
    );
  }
  return (
    <div
      key={key}
      data-contract-field="true"
      data-field-type={type}
      style={{
        position: "absolute",
        left: a.x ?? 0,
        top: a.y ?? 0,
        width: a.width ?? 200,
        height: a.height ?? 80,
      }}
    >
      <div
        className="flex h-full w-full items-center justify-center rounded bg-white/30 px-2 text-center"
        style={{ outline: `1.5px dashed ${color}`, color, fontSize: 10, fontWeight: 600 }}
      >
        {fieldIcon(type)} {label}
      </div>
    </div>
  );
};

export const fieldColor = (type: string): string => {
  if (type === "signature") return "#F472B6";
  if (type === "seal") return "#C8102E";
  if (type === "checkbox") return "#22A55B";
  if (type === "date") return "#FF9934";
  if (type === "address") return "#0EA5E9";
  if (type === "text") return "#7D6DFF";
  return "#999";
};

export const fieldIcon = (type: string): string => {
  if (type === "signature") return "✏️";
  if (type === "seal") return "🖋";
  if (type === "checkbox") return "☑️";
  if (type === "date") return "📅";
  if (type === "address") return "📍";
  if (type === "text") return "🔤";
  return "🔵";
};

const tableColumnWidths = (rows: any[]): number[] => {
  const firstUsableRow =
    rows.find((row: any) =>
      (row.content || []).some(
        (cell: any) => Array.isArray(cell.attrs?.colwidth) && cell.attrs.colwidth.length,
      ),
    ) || rows[0];
  const raw: number[] = [];
  (firstUsableRow?.content || []).forEach((cell: any) => {
    const colspan = cell.attrs?.colspan || 1;
    const colwidth = Array.isArray(cell.attrs?.colwidth) ? cell.attrs.colwidth : [];
    if (colwidth.length) {
      for (let i = 0; i < colspan; i += 1) raw.push(Number(colwidth[i]) || 0);
    } else {
      for (let i = 0; i < colspan; i += 1) raw.push(0);
    }
  });
  const count = Math.max(
    1,
    raw.length ||
      (rows[0]?.content || []).reduce(
        (sum: number, cell: any) => sum + (cell.attrs?.colspan || 1),
        0,
      ),
  );
  const filled = raw.length ? raw : Array(count).fill(CONTRACT_BODY_WIDTH / count);
  const known = filled.filter((w) => w > 0);
  const fallback = known.length
    ? known.reduce((sum, w) => sum + w, 0) / known.length
    : CONTRACT_BODY_WIDTH / count;
  const completed = filled.map((w) => (w > 0 ? w : fallback));
  const total = completed.reduce((sum, w) => sum + w, 0) || CONTRACT_BODY_WIDTH;
  return completed.map((w) => Math.max(1, Math.round((w / total) * CONTRACT_BODY_WIDTH)));
};

const isCenteredSingleCell = (cell: any): boolean =>
  (cell.content || []).some((inner: any) => {
    if (inner?.type !== "paragraph") return false;
    const textAlign = inner.attrs?.textAlign || "";
    return textAlign === "center";
  });

export const ContractDocumentSurface = ({
  doc,
  scale = 1,
  variables,
  renderField = defaultFieldRenderer,
  renderVariable,
  variableMode = "label",
  pageShadow = true,
  clipPages = true,
}: Props) => {
  const variableRenderer = renderVariable || defaultVariableRenderer(variables, variableMode);
  const pages = Array.isArray(doc?.content)
    ? doc.content.filter((n: any) => n?.type === "contractPage")
    : [];
  const renderPages = pages.length
    ? pages
    : [{ type: "contractPage", attrs: { width: CONTRACT_A4_WIDTH, height: CONTRACT_A4_HEIGHT }, content: doc?.content || [] }];

  const renderInlineList = (nodes: any[] = [], path: string): ReactNode =>
    nodes.map((node, i) => renderInline(node, `${path}-${i}`));

  const renderParagraphContent = (nodes: any[] = [], path: string): ReactNode => {
    if (!nodes.length) return <br />;
    return renderInlineList(nodes, path);
  };

  const renderTextWithMarkers = (
    text: string,
    marks: any[] | undefined,
    key: string,
  ): ReactNode => {
    if (!text) return <span key={key} />;
    TEXT_MARKER_RE.lastIndex = 0;
    const parts: ReactNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;
    while ((match = TEXT_MARKER_RE.exec(text)) !== null) {
      if (match.index > cursor) {
        parts.push(
          <span key={`${key}-t-${cursor}`} style={markStyle(marks)}>
            {text.slice(cursor, match.index)}
          </span>,
        );
      }
      const rawKey = (match[1] || match[2] || "").trim();
      const varKey = variableKeyFromDisplay(rawKey);
      parts.push(variableRenderer(varKey, variableDisplayLabel(rawKey), `${key}-v-${match.index}`));
      cursor = TEXT_MARKER_RE.lastIndex;
    }
    if (!parts.length) {
      return (
        <span key={key} style={markStyle(marks)}>
          {text}
        </span>
      );
    }
    if (cursor < text.length) {
      parts.push(
        <span key={`${key}-t-${cursor}`} style={markStyle(marks)}>
          {text.slice(cursor)}
        </span>,
      );
    }
    return <span key={key}>{parts}</span>;
  };

  const renderInline = (node: any, key: string): ReactNode => {
    if (!node) return null;
    if (node.type === "text") return renderTextWithMarkers(node.text || "", node.marks, key);
    if (node.type === "hardBreak") return <br key={key} />;
    if (node.type === "contractVariable") {
      const varKey = node.attrs?.varKey || "";
      const label = node.attrs?.label || variableDisplayLabel(varKey);
      return variableRenderer(varKey, label, key);
    }
    if (node.type === "contractField") {
      // inline 필드도 주입된 renderField(인터랙티브 or 기본)로 렌더 — 블록 필드와 동일 경로.
      // renderContractField/defaultFieldRenderer 가 x/y 없으면 inline 으로 그린다.
      return renderField(node, key);
    }
    return renderInlineList(node.content || [], key);
  };

  const renderBlock = (node: any, index: number): ReactNode => {
    const type = node?.type;
    if (type === "paragraph") {
      return (
        <p key={index} style={paragraphStyle(node)}>
          {renderParagraphContent(node.content || [], `p-${index}`)}
        </p>
      );
    }
    if (type === "heading") {
      const level = node.attrs?.level || 3;
      const Tag = (`h${Math.min(Math.max(level, 1), 3)}`) as keyof JSX.IntrinsicElements;
      return (
        <Tag key={index} style={headingStyle(node)}>
          {renderInlineList(node.content || [], `h-${index}`)}
        </Tag>
      );
    }
    if (type === "table") return renderTable(node, `t-${index}`);
    if (type === "positionedText") return renderPositionedText(node, `pt-${index}`);
    if (type === "positionedImage") return renderPositionedImage(node, `pi-${index}`);
    if (type === "contractField") return renderField(node, `f-${index}`);
    return null;
  };

  const renderTable = (node: any, key: string): ReactNode => {
    const rows = node.content || [];
    const columnWidths = tableColumnWidths(rows);
    return (
      <table key={key} style={tableStyle}>
        <colgroup>
          {columnWidths.map((width, i) => (
            <col key={`${key}-col-${i}`} style={{ width, maxWidth: width }} />
          ))}
        </colgroup>
        <tbody>
          {rows.map((row: any, ri: number) => {
            const cells = row.content || [];
            const singleCell = cells.length === 1;
            return (
              <tr key={`${key}-r-${ri}`}>
                {cells.map((cell: any, ci: number) => {
                  const isHeader = cell.type === "tableHeader";
                  const Tag = (isHeader ? "th" : "td") as keyof JSX.IntrinsicElements;
                  const headerLike = isHeader || (singleCell && isCenteredSingleCell(cell));
                  return (
                    <Tag
                      key={`${key}-c-${ri}-${ci}`}
                      colSpan={cell.attrs?.colspan || 1}
                      rowSpan={cell.attrs?.rowspan || 1}
                      style={tableCellStyle(headerLike, singleCell)}
                    >
                      {(cell.content || []).map((inner: any, ii: number) =>
                        inner.type === "paragraph" ? (
                          <p key={ii} style={tableParagraphStyle(inner)}>
                            {renderParagraphContent(inner.content || [], `${key}-cell-${ri}-${ci}-${ii}`)}
                          </p>
                        ) : null,
                      )}
                    </Tag>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderPositionedText = (node: any, key: string): ReactNode => {
    const a = node.attrs || {};
    return (
      <div
        key={key}
        data-positioned-text="true"
        style={{
          position: "absolute",
          left: a.x ?? 0,
          top: a.y ?? 0,
          minWidth: a.width ?? 200,
          fontSize: `${a.fontSize || 11}pt`,
          fontFamily: a.fontFamily || CONTRACT_EDITOR_FONT,
          fontWeight: a.bold ? 700 : 400,
          color: a.color || "#111",
          textAlign: a.align || "left",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {renderInlineList(node.content || [], key)}
      </div>
    );
  };

  const renderPositionedImage = (node: any, key: string): ReactNode => {
    const a = node.attrs || {};
    return (
      <img
        key={key}
        src={a.src}
        alt={a.alt || ""}
        style={{
          position: "absolute",
          left: a.x ?? 0,
          top: a.y ?? 0,
          width: a.width ?? 200,
          height: a.height ?? 100,
          objectFit: "contain",
        }}
      />
    );
  };

  return (
    <>
      {renderPages.map((page: any, pageIndex: number) => {
        const width = page.attrs?.width || CONTRACT_A4_WIDTH;
        const height = page.attrs?.height || CONTRACT_A4_HEIGHT;
        return (
          <div
            key={pageIndex}
            style={{
              width: width * scale,
              height: height * scale,
              margin: "0 auto 12px",
              overflow: "visible",
            }}
          >
            <div
              className="contract-document-page"
              data-contract-page="true"
              data-page-number={String(page.attrs?.pageNumber ?? pageIndex + 1)}
              data-page-width={String(width)}
              style={{
                position: "relative",
                width,
                height,
                minWidth: width,
                maxWidth: width,
                minHeight: height,
                maxHeight: height,
                background: "#fff",
                boxShadow: pageShadow ? "0 2px 8px rgba(0,0,0,0.08)" : undefined,
                // clipPages=false(서명/열람): 넘치는 내용을 자르지 않는다(변수 값이 채워져 줄이 늘어도 씹히지 않게).
                // contain:strict 는 size+paint 컨테인이라 클리핑을 유발하므로 함께 해제.
                overflow: clipPages ? "hidden" : "visible",
                contain: clipPages ? "strict" : undefined,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                boxSizing: "border-box",
              }}
            >
              <div aria-hidden="true" style={{ height: CONTRACT_PAGE_MARGIN, width: "100%" }} />
              {(page.content || []).map((node: any, i: number) => renderBlock(node, i))}
            </div>
          </div>
        );
      })}
    </>
  );
};

const paragraphStyle = (node: any): CSSProperties => {
  const attrs = node.attrs || {};
  return {
    margin: 0,
    marginLeft: attrs.marginLeft || CONTRACT_PAGE_MARGIN,
    marginRight: attrs.marginRight || CONTRACT_PAGE_MARGIN,
    paddingTop: 0,
    paddingLeft: attrs.hwpHangingIndent ? "1.6em" : undefined,
    width: CONTRACT_BODY_WIDTH,
    maxWidth: CONTRACT_BODY_WIDTH,
    boxSizing: "border-box",
    fontFamily: CONTRACT_EDITOR_FONT,
    fontSize: attrs.fontSize || "10pt",
    color: "#111",
    lineHeight: attrs.lineHeight || 1.5,
    letterSpacing: "-0.04em",
    textAlign: attrs.textAlign || "left",
    textIndent: attrs.hwpHangingIndent ? "-1.6em" : attrs.textIndent || undefined,
    whiteSpace: "pre-wrap",
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
    tabSize: 4,
    breakBefore: attrs.pageBreakBefore ? "page" : undefined,
  };
};

const headingStyle = (node: any): CSSProperties => {
  const level = node.attrs?.level || 3;
  const fontSize = level === 1 ? "18pt" : level === 2 ? "14pt" : "12pt";
  return {
    ...paragraphStyle(node),
    fontSize,
    fontWeight: 700,
    lineHeight: 1.35,
  };
};

const tableStyle: CSSProperties = {
  borderCollapse: "collapse",
  margin: `6pt ${CONTRACT_PAGE_MARGIN}px`,
  tableLayout: "fixed",
  width: CONTRACT_BODY_WIDTH,
  minWidth: CONTRACT_BODY_WIDTH,
  maxWidth: CONTRACT_BODY_WIDTH,
  boxSizing: "border-box",
  fontFamily: CONTRACT_EDITOR_FONT,
  fontSize: "11pt",
};

const tableCellStyle = (headerLike: boolean, singleCell: boolean): CSSProperties => ({
  border: "0.6pt solid #969696",
  padding: singleCell ? "2pt 0" : "2pt 4pt",
  verticalAlign: "middle",
  lineHeight: 1.3,
  background: headerLike ? "#F5F5F5" : "#fff",
  fontWeight: headerLike ? 700 : 400,
  // 한글은 어절 단위로 접고(keep-all), 이메일·URL 등 긴 토큰만 강제로 끊는다(anywhere).
  // overflow 는 visible — 값이 길어 줄이 늘면 셀 높이가 커지도록(클리핑 방지).
  wordBreak: "keep-all",
  overflowWrap: "anywhere",
  overflow: "visible",
});

const tableParagraphStyle = (node: any): CSSProperties => {
  const attrs = node.attrs || {};
  return {
    margin: 0,
    marginLeft: 0,
    marginRight: 0,
    paddingTop: 0,
    paddingLeft: attrs.hwpHangingIndent ? "1.6em" : undefined,
    maxWidth: "none",
    lineHeight: attrs.lineHeight || 1.3,
    letterSpacing: "-0.04em",
    textAlign: attrs.textAlign || "left",
    textIndent: attrs.hwpHangingIndent ? "-1.6em" : attrs.textIndent || undefined,
    whiteSpace: "pre-wrap",
    wordBreak: "keep-all",
    overflowWrap: "anywhere",
  };
};

export default ContractDocumentSurface;
